import * as Yup from 'yup';

import { Alert, Button, Form } from 'react-bootstrap';
import { Field, Formik } from 'formik';
import React, { useContext, useEffect } from 'react';
import { contentfulPropTypes, onboardingPropTypes } from '../../propTypes';
import {
  fakeRejectedRequest,
  getAddressesByPostcode,
  getMeterDetailsForAddressId,
} from '../../api/Location';
import { flowSteps, recordFlowProgress } from '../../utils/recordFlowProgress';

import { Auth } from 'aws-amplify';
import CloseButton from '../../ui/Styled/CloseButton';
import { ConsentContext } from '../../context/ConsentContext';
import ErrorLabel from '../../ui/ErrorLabel';
import LoopSpinner from '../../ui/LoopSpinner';
import OnboardingDeleteAccountModal from '../../ui/Modal/DeleteAccount/OnboardingDeleteAccountModal';
import ReactMarkdown from 'react-markdown';
import cx from 'classnames';
import { getAddressById } from '../../utils/addressHelperUtil';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { usePersistedState } from '../../hooks';
import withContentfulTag from '../../utils/withContentfulTag';

const StyledDeleteAccountParagraph = styled.p`
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 0;

  button {
    font-size: 14px;
    line-height: 1.5;
  }
`;

const AddressContainer = ({ contentful, loading, fetched }) => {
  const navigate = useNavigate();
  const {
    setIsManualAddress,
    setLookedUpMpanKnown,
    setLookedUpMpanHash,
    setLookedUpMpanObfuscated,
    consentFlow,
    showDeleteAccountModal,
    setShowDeleteAccountModal,
    isDeleting,
    setIsDeleting,
    deleteError,
    setDeleteError,
    showAlert,
    setShowAlert,
    alert,
    setAlert,
  } = useContext(ConsentContext);

  useEffect(() => {
    const asyncRecordFlowProgress = async () => {
      await recordFlowProgress(flowSteps.WHAT_IS_YOUR_ADDRESS_1);
    };
    asyncRecordFlowProgress();
  }, []);

  const postcodeValidationRegex =
    '^([Gg][Ii][Rr]' +
    ' 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y]' +
    '[0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y]' +
    '[0-9]?[A-Za-z])))) ?[0-9][A-Za-z]{2})$';

  const postcodeSchema = Yup.object().shape({
    postcode: Yup.string()
      .test('len', 'Please enter a UK postcode', val => val?.length >= 6)
      .matches(postcodeValidationRegex, 'Please enter a UK postcode')
      .required('Please enter your postcode'),
  });

  const addressSchema = Yup.object().shape({
    addressId: Yup.string().trim().required('Please select an address'),
  });

  const [postcode, setPostcode] = usePersistedState('postcode', undefined);
  const [isForceApiFailPostcode, setIsForceApiFailPostcode] = usePersistedState(
    'isForceApiFailPostcode',
    false,
  );
  const [addresses, setAddresses] = usePersistedState('addresses', []);
  const [showManualAddressOption, setShowManualAddressOption] =
    usePersistedState('showManualAddressOption', false);

  const onSubmitPostcode = async ({ postcode }) => {
    setAddresses([]);
    const fakePostcode = /^hp99\s?1ab/i;
    if (fakePostcode.test(postcode)) {
      postcode = 'SW1A 1AA';
      setIsForceApiFailPostcode(true);
      setPostcode(postcode);
    } else {
      setIsForceApiFailPostcode(false);
      setPostcode(postcode.toUpperCase());
    }
    hideAlert();
    try {
      await getAddressesByPostcode(postcode.toUpperCase()).then(
        ({ addresses }) => {
          setAddresses(addresses);
        },
      );
    } catch (error) {
      const message =
        error?.response?.body?.message ||
        `Error: No addresses found for this postcode: ${postcode}. ` +
          'Note: Only UK mainland addresses are currently supported by Loop.';

      addAlert('danger', message);

      setPostcode(undefined);
    }
    setShowManualAddressOption(true);
    await recordFlowProgress(flowSteps.WHAT_IS_YOUR_ADDRESS_2);
  };

  const onSubmitAddress = async ({ addressId }) => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      let address;
      if (addressId.includes('|')) {
        address = addressId;
      } else {
        const addressById = getAddressById(addressId, addresses);
        address = addressById.id;
      }
      const attributes = {
        address,
        'custom:smart_meter_user': 'false',
        'custom:postcode': postcode,
      };
      await Auth.updateUserAttributes(user, attributes);
      window.scrollTo(0, 0);
      try {
        if (isForceApiFailPostcode) {
          await fakeRejectedRequest();
        } else {
          await getMeterDetailsForAddressId(addressId).then(meterDetails => {
            setLookedUpMpanKnown(meterDetails?.mpanKnown);
            setLookedUpMpanHash(meterDetails?.mpanHash);
            setLookedUpMpanObfuscated(meterDetails?.mpanObfuscated);
          });
        }
      } catch (error) {
        setLookedUpMpanKnown(false);
        setLookedUpMpanHash('');
        setLookedUpMpanObfuscated('');
      }

      setIsManualAddress(false);

      navigate(`/${consentFlow}/move-date`);
    } catch (error) {
      navigate(`/${consentFlow}/system-unavailable`);
    }
  };

  const onReset = e => {
    e.preventDefault();
    setPostcode(undefined);
    setShowManualAddressOption(false);
  };

  const onNoAddress = e => {
    navigate(`/${consentFlow}/manual-address`);
  };

  const addAlert = (variant, message) => {
    setShowAlert(true);
    setAlert({ variant, message });
  };

  const hideAlert = () => {
    setShowAlert(false);
    setAlert(undefined);
  };

  return (
    !loading &&
    fetched && (
      <>
        <OnboardingDeleteAccountModal
          show={showDeleteAccountModal}
          onHide={() => setShowDeleteAccountModal(false)}
          setIsDeleting={setIsDeleting}
          deleteError={deleteError}
          setDeleteError={setDeleteError}
          initialReason="Didn't complete onboarding"
        />
        <p>
          Step <strong>1</strong> of <strong>3</strong>
        </p>
        <h1 className="mb-3">{contentful?.title}</h1>
        <div>
          {!postcode && (
            <Formik
              validationSchema={postcodeSchema}
              initialValues={{ postcode: '' }}
              onSubmit={onSubmitPostcode}
              validateOnChange>
              {({ handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  {showAlert && alert && (
                    <div>
                      <Alert
                        variant={alert.variant}
                        onClose={() => hideAlert()}>
                        {alert.message}
                        <CloseButton onClick={() => hideAlert()} />
                      </Alert>
                    </div>
                  )}
                  <p>{contentful?.subtitle}</p>
                  <Field name="postcode">
                    {({ field, meta }) => (
                      <Form.Group controlId="postcode" className="text-left">
                        <Form.Label>Postcode</Form.Label>
                        <Form.Control
                          type="text"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          isValid={!meta.error && meta.touched}
                          placeholder="Postcode"
                          className={cx({ error: !!meta.error })}
                        />
                        {meta.error && meta.touched && (
                          <ErrorLabel label={meta.error} />
                        )}
                      </Form.Group>
                    )}
                  </Field>

                  {showManualAddressOption && (
                    <Form.Group className="noAddress-text text-left">
                      <div>
                        Address not listed?
                        <Button
                          variant="link"
                          onClick={onNoAddress}
                          className="p-0 noAddress-text">
                          Click here
                        </Button>
                      </div>
                    </Form.Group>
                  )}

                  {contentful?.primaryButtonText && (
                    <Form.Group className="pb-0">
                      <Button variant="primary" className="w-100" type="submit">
                        {contentful.primaryButtonText}
                      </Button>
                    </Form.Group>
                  )}
                </Form>
              )}
            </Formik>
          )}
          {postcode && (
            <>
              <ReactMarkdown>{contentful?.body}</ReactMarkdown>
              <div className="text-left">
                <Form.Group className="mb-4">
                  <div>
                    <Form.Label>Postcode</Form.Label>
                  </div>
                  <div>
                    {postcode}{' '}
                    <Button variant="link" onClick={onReset} className="p-0">
                      Change
                    </Button>
                  </div>
                </Form.Group>
                {addresses.length > 0 ? (
                  <Formik
                    validationSchema={addressSchema}
                    onSubmit={onSubmitAddress}
                    initialValues={{
                      address: undefined,
                    }}
                    validateOnChange>
                    {({ handleSubmit, isValid, isSubmitting }) => (
                      <Form onSubmit={handleSubmit}>
                        <Field name="addressId">
                          {({ field, meta }) => (
                            <Form.Group controlId="addressId">
                              <Form.Label>Select your address</Form.Label>
                              <Form.Control
                                as="select"
                                size="md"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                isValid={!meta.error && meta.touched}
                                placeholder="Select your address"
                                className={cx({ error: !!meta.error })}>
                                <optgroup>
                                  <option value="" key="address-select">
                                    Select your address
                                  </option>
                                  {addresses.map(
                                    ({ id, apiId, address }, index) => (
                                      <option
                                        key={`address-${index}`}
                                        value={apiId || id}>
                                        {address}
                                      </option>
                                    ),
                                  )}
                                </optgroup>
                              </Form.Control>
                              {meta.error && meta.touched && (
                                <ErrorLabel label={meta.error} />
                              )}
                            </Form.Group>
                          )}
                        </Field>
                        <Form.Group className="noAddress-text">
                          <div>
                            Address not listed?
                            <Button
                              variant="link"
                              onClick={onNoAddress}
                              className="p-0 noAddress-text">
                              Click here
                            </Button>
                          </div>
                        </Form.Group>
                        <Form.Group>
                          {!isSubmitting && contentful?.secondaryButtonText ? (
                            <Button
                              variant="primary"
                              disabled={!isValid || isSubmitting}
                              className="w-100"
                              type="submit">
                              {contentful.secondaryButtonText}
                            </Button>
                          ) : (
                            <LoopSpinner />
                          )}
                        </Form.Group>
                      </Form>
                    )}
                  </Formik>
                ) : (
                  <LoopSpinner />
                )}
              </div>
            </>
          )}
        </div>

        <div>
          <StyledDeleteAccountParagraph>
            No longer want a Loop account?{' '}
            <Button
              variant="inline-link"
              onClick={() => setShowDeleteAccountModal(true)}
              disabled={isDeleting}>
              Delete it here
            </Button>
          </StyledDeleteAccountParagraph>
        </div>
      </>
    )
  );
};

AddressContainer.propTypes = {
  ...contentfulPropTypes,
  ...onboardingPropTypes,
};

export default withContentfulTag(
  AddressContainer,
  'web_app_onboarding_address_page',
);
